import React from "react";
import {
  Compass,
  Sparkles,
  PhoneCall,
  Brain,
  MessageCircle,
  ShieldCheck,
  Target,
  TrendingUp,
} from "lucide-react";
import CourseBrochure from "./CourseBrochure";
import SEO from "./SEO";

const ROOKIE = {
  slug: "rookie-hunter",
  name: "Rookie Hunter",
  eyebrow: "Rookie Hunter · A 2-Day B2B Sales Sprint",
  coverTitle: {
    pre: "Turn your",
    accent: "B2B sales job",
    post: "into a hunting career.",
  },
  coverPromise:
    "A foundational sales training for SDRs, BDRs, inside-sales, and field-sales professionals in their first five years in B2B — SaaS, industrial, services. Two immersive days, 10 AM to 6 PM. Sixty-plus scripts across email, phone, and LinkedIn. Built by 18 years of enterprise field expertise.",
  advisor: {
    role: "Programme Advisor · Nagpur",
    name: "Sweta Bhushan",
    phone: "+91 95036 73348",
    email: "sweta.bhushan@sebyx.com",
  },

  stats: [
    { k: "2 Days", v: "10 AM – 6 PM" },
    { k: "8 Modules", v: "32 lessons" },
    { k: "60% Drills", v: "Cold calls & role-play" },
    { k: "Certified", v: "Sebyx Rookie Hunter" },
  ],
  whoItsFor: [
    "B2B sales professionals with 0–5 years of experience — SDRs, BDRs, inside-sales, and field-sales executives.",
    "Junior sales teams at SaaS, industrial, and services companies who need to build pipeline from scratch.",
    "Founders and sales heads hiring their first sales cohort and needing a repeatable playbook.",
    "Career-switchers moving from support, marketing, or operations into B2B sales.",
  ],
  whatYouGet: [
    "Sebyx Rookie Hunter Certification (printed + LinkedIn)",
    "40-page printed Playbook (yours to keep)",
    "60+ scripts — email, phone, and LinkedIn",
    "Alumni WhatsApp group access",
    "30-60-90 day pipeline scorecard",
    "Priority seat for the Master Hunter cohort",
  ],
  outcomes: [
    {
      title: "Cold outreach that gets replies",
      detail: "Email openers, phone scripts, and LinkedIn DMs that actually get opened, read, and responded to.",
    },
    {
      title: "Discovery calls that convert",
      detail: "The 5-question framework that turns a first call into a qualified opportunity, not a dead lead.",
    },
    {
      title: "Objection-proof closes",
      detail: "Handle \"no budget\", \"not now\", and \"we have a vendor\" without discounting or backing off.",
    },
    {
      title: "A working pipeline in 90 days",
      detail: "CRM discipline, 7-touch follow-up cadence, and multi-threading — the habits senior reps live by.",
    },
  ],
  agenda: [
    [
      { time: "10:00 · 11:30", title: "M1 — The Sales Career Mindset" },
      { time: "11:30 · 13:00", title: "M2 — Professional Presence · Digital & Live" },
      { time: "14:00 · 15:30", title: "M3 — Cold Outreach That Gets Replies (live)" },
      { time: "15:30 · 17:00", title: "M4 — B2B Buyer Psychology" },
      { time: "17:00 · 18:00", title: "Debrief · outreach homework" },
    ],
    [
      { time: "10:00 · 11:30", title: "M5 — The Discovery Call (live role-play)" },
      { time: "11:30 · 13:00", title: "M6 — Objection Handling" },
      { time: "14:00 · 15:30", title: "M7 — The Close & Multi-Threading" },
      { time: "15:30 · 17:00", title: "M8 — CRM, Follow-Up & Referrals" },
      { time: "17:00 · 18:00", title: "Final drill · certification · Q&A" },
    ],
  ],
  modules: [
    {
      n: "M1",
      hours: "90 min",
      Icon: Compass,
      title: "The Sales Career Mindset",
      lines: [
        "Sales as a real career",
        "5-year income math",
        "Amateur vs. professional",
      ],
    },
    {
      n: "M2",
      hours: "90 min",
      Icon: Sparkles,
      title: "Professional Presence — Digital & Live",
      lines: [
        "Your LinkedIn as a sales asset",
        "Voice on a call · body on Zoom",
        "Building professional equity",
      ],
    },
    {
      n: "M3",
      hours: "90 min",
      Icon: PhoneCall,
      title: "Cold Outreach That Gets Replies",
      lines: [
        "Email openers that don't bounce",
        "The 15-second phone opener",
        "LinkedIn DM sequences",
      ],
    },
    {
      n: "M4",
      hours: "90 min",
      Icon: Brain,
      title: "B2B Buyer Psychology",
      lines: [
        "Why companies actually buy",
        "Mapping the decision unit",
        "Champion vs. blocker signals",
      ],
    },
    {
      n: "M5",
      hours: "90 min",
      Icon: MessageCircle,
      title: "The Discovery Call",
      lines: [
        "The 5-question framework",
        "Selling outcomes, not features",
        "Handoff to demo · next step",
      ],
    },
    {
      n: "M6",
      hours: "90 min",
      Icon: ShieldCheck,
      title: "Objection Handling",
      lines: [
        "\"Send me an email\"",
        "\"No budget\" · \"Not now\"",
        "\"We already have a vendor\"",
      ],
    },
    {
      n: "M7",
      hours: "90 min",
      Icon: Target,
      title: "The Close & Multi-Threading",
      lines: [
        "Closing your first deal",
        "Multi-threading a stalled deal",
        "Ethical urgency (never fake)",
      ],
    },
    {
      n: "M8",
      hours: "90 min",
      Icon: TrendingUp,
      title: "CRM, Follow-Up & Referrals",
      lines: [
        "CRM discipline from Day 1",
        "The 7-touch follow-up cadence",
        "Asking for the first referral",
      ],
    },
  ],
};

export default function RookieHunterBrochure() {
  return (
    <>
      <SEO
        title="Rookie Hunter Bootcamp | B2B Enterprise Lead Generation"
        description="Build a predictable pipeline in 90 days. A 2-day intensive sprint for SDRs, BDRs, and founders. Cold outreach scripts, discovery calls, and objection handling."
        path="/rookie-hunter-brochure"
      />
      <CourseBrochure course={ROOKIE} />
    </>
  );
}
